/**
 * SA-1387: the Mailboxes page's words and the small sentence rules behind
 * them. Pure. Moved out of MailboxesPage.tsx so the list, the pane and the
 * page read one copy.
 */
import { ApiError } from '@/services/apiClient'
import type { Mailbox, MailboxChannel, MailboxPolicyChange, MailboxProvider } from '@/services/mailboxes'
import type { IntakeRow } from '@/utils/intakeRelation'
import { startingProcess } from '@/utils/intakeRelation'
import { indefiniteArticle } from '@/utils/article'
import { agendaLabel } from '@/utils/workflowOverview'
import { refusalText } from './connectErrorText'
import { kindCount } from './routerPolicyModel'
import type { MailboxTab } from './mailboxListModel'

export const STATUS_BADGE: Record<
  Mailbox['status'],
  { label: string; variant: 'success' | 'secondary' | 'destructive' | 'outline' }
> = {
  // SA-947: a WhatsApp group source waits here for its link and its group.
  connecting: { label: 'Linking', variant: 'secondary' },
  active: { label: 'Active', variant: 'success' },
  paused: { label: 'Paused', variant: 'secondary' },
  reauth_required: { label: 'Reauth needed', variant: 'destructive' },
  unconnected: { label: 'Unconnected', variant: 'outline' },
}

// SA-101: wire codes from MailboxRead.binding_warning.
export const BINDING_WARNING_LABEL: Record<string, string> = {
  workflow_missing: 'Workflow missing',
  workflow_archived: 'Workflow archived',
  workflow_no_version: 'No published version',
  workflow_not_intake_agenda: 'Not a quote intake agenda',
  // SA-716: router mailboxes.
  policy_invalid: 'Routing policy unreadable',
  handler_unavailable: 'Handler not available',
  // SA-945: a kind whose handler this source's channel does not serve.
  handler_unavailable_for_channel: 'Handler not available on this source',
}

export const BINDING_WARNING_FALLBACK = 'Workflow binding broken'

export const NEW_MAILBOX = 'New mailbox'
export const PROVIDER_SETTINGS = 'Provider settings'
export const PROVIDER_SETTINGS_MISSING = 'Provider settings, a provider has no client yet'
export const NOT_SAVED_YET = 'not saved yet'
export const NO_CLIENT_YET = 'no client yet'
export const EMPTY_LIST = 'No mailboxes yet. Add one with New mailbox to start ingesting a group inbox.'
export const REAUTH_ADVICE = 'Connect links it again; Pause stops the alerts.'
export const RECEIVING_NORMALLY = 'Receiving normally'
export const NONE = 'None'
/** A persona or an owner the mailbox names but the page has not loaded (never the bare id, SA-330). */
export const UNKNOWN = 'Unknown'
export const MORE_ACTIONS = 'More actions'
export const EDIT_ROUTING = 'Edit routing'
export const USE_A_ROUTER = 'Use a router'
export const LIST_LABEL = 'Mailboxes'
export const PROVIDER_SETTINGS_SENTENCE = "Register each provider's OAuth client before connecting a mailbox."

/** Overview's section headings and Connection's facts (spec §5.4). */
export const OVERVIEW_WORDS = {
  connection: 'Connection',
  whatItDoes: 'What it does',
  outbound: 'Outbound',
  account: 'Account',
  health: 'Health',
  persona: 'Persona',
  owner: 'Owner',
} as const

export const PARKED_TIP = 'Dead-lettered messages skipped by ingestion'
export const POST_COMMIT_TIP =
  'Intake committed, but a later step failed — the conversation exists; follow-up work (e.g. the workflow turn or reply) may be missing'

export function parkedTip(lastParkedAt: string | null): string {
  return lastParkedAt ? `${PARKED_TIP} · last parked ${new Date(lastParkedAt).toLocaleString()}` : PARKED_TIP
}

/** The pane's banner: the row's attention sentence, and on a reauth what the person can do. */
export function attentionBanner(text: string, status: Mailbox['status']): string {
  return status === 'reauth_required' ? `${text}. ${REAUTH_ADVICE}` : text
}

export const TAB_LABEL: Record<MailboxTab, string> = {
  overview: 'Overview',
  routing: 'Routing',
  triage: 'Triage',
}

export const GROUP_HEADING: Record<MailboxChannel, string> = {
  email: 'Email',
  'whatsapp-group': 'WhatsApp groups',
}

export function moreActionsLabel(name: string): string {
  return `More actions for ${name}`
}

export function discardRoutingConfirm(name: string): string {
  return `Discard your changes to ${name}'s routing?`
}

export function parkedText(n: number): string {
  return `${n} parked`
}

export function postCommitText(n: number): string {
  return `${n} post-commit failure${n === 1 ? '' : 's'}`
}

export function providerLabel(id: string, providers: readonly MailboxProvider[]): string {
  return providers.find((p) => p.id === id)?.label ?? id
}

/** The router's waiting clauses, e.g. `['3 awaiting approval', '1 needs a kind']`. */
export function waitingWords(mb: Mailbox): string[] {
  const waiting = mb.triage_waiting
  return [
    waiting && waiting.awaiting_approval > 0 ? `${waiting.awaiting_approval} awaiting approval` : null,
    waiting && waiting.needs_triage > 0 ? `${waiting.needs_triage} ${waiting.needs_triage === 1 ? 'needs' : 'need'} a kind` : null,
  ].filter((w): w is string => w !== null)
}

export function sinkLabel(mb: Mailbox, workflows: readonly IntakeRow[] | undefined, engine: boolean | null): string {
  if (mb.sink_type === 'router') {
    const kinds = kindCount(mb.sink_config)
    return [`Router · ${kinds} ${kinds === 1 ? 'kind' : 'kinds'}`, ...waitingWords(mb)].join(' · ')
  }
  const bound = typeof mb.sink_config?.workflow_id === 'string' ? mb.sink_config.workflow_id : null
  if (mb.sink_type === 'record') {
    if (!bound) return 'Quote record → ?'
    // SA-469 (spec §3): the agenda by the map's rule, and the process it starts at once. The agenda
    // is named by `agendaLabel` on purpose, never the library title: "Shipment quote intake" would
    // print "intake intake". SA-476 (spec D2): the start only when it will happen.
    const start = startingProcess(bound, workflows, engine)
    return `Quote record → ${agendaLabel(bound)} intake${start.kind === 'starts' ? ` (starts ${start.process.name})` : ''}`
  }
  if (mb.sink_type === 'workflow') return `Workflow → ${bound ?? '?'}`
  return 'Staff conversation'
}

/** SA-469 (spec §3): a record sink starts the bound process at once — SA-476: only when it will; the article agrees with the noun. */
export function recordSinkSentence(noun: string, processName: string | null): string {
  const becomes = `Each new thread becomes ${indefiniteArticle(noun)} ${noun}`
  if (!processName) {
    return `${becomes}; fields are extracted from the email, the rest is completed on the record. No reply is sent.`
  }
  return `${becomes} and its ${processName} process starts at once; fields are extracted from the email, the rest is completed on the record. No reply is sent.`
}

// ApiError.message is the raw HTTP status line ("POST ... failed: ...");
// the actionable reason is the server's `detail` (FastAPI's {detail: ...}
// envelope), stashed separately — prefer that when present. Mirrors
// EditServicePanel's errorMessage / WorkflowSimulatePage's describeStartError.
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && typeof err.detail === 'string') return err.detail
  // SA-947 (spec P11): a {code, message} refusal is worded once, never a bare code.
  const refusal = refusalText(err)
  if (refusal) return refusal
  if (err instanceof Error) return err.message
  return fallback
}

/** SA-1080 (spec §5.2): the routing save refused because the stored policy moved after the
 *  editor's check (409 `policy_changed`). The editor checks again; no sentence is shown. */
export function isPolicyChanged(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false
  const detail = err.detail
  return typeof detail === 'object' && detail !== null && (detail as { code?: unknown }).code === 'policy_changed'
}

/** SA-1422: the email mailbox's delete confirm; the group source keeps its own wording. */
export const emailDeleteConfirm = (name: string): string =>
  `Delete mailbox "${name}"? This also deletes its record of which threads it has read, so replies to earlier threads will arrive as new conversations. This cannot be undone.`

/** SA-1422 (spec §4.5): Change account, Confirm and the refusal banner's offered action. */
export const CHANGE_ACCOUNT = 'Change account'
export const CONFIRM_ACCOUNT = 'Confirm'
export const CHANGE_ACCOUNT_TITLE = "Which account's inbox should this mailbox read?"
export const ACCOUNT_FIELD = 'Account address'
export const CONTINUE = 'Continue'
export const CANCEL = 'Cancel'
/** Confirm's 409 `account_changed`: someone reconnected or edited the mailbox meanwhile. */
export const ACCOUNT_CHANGED = 'This mailbox changed meanwhile. Check it and try again.'
export const CONFIRM_FAILED = 'Failed to confirm the account'

export const connectAsLabel = (account: string): string => `Connect as ${account}`

/** What the routing editor's last-change line reads from the newest history row. */
export type LastChange = Pick<MailboxPolicyChange, 'changed_by_name' | 'changed_at' | 'fields'>

// SA-1421: the subjects other than the binding, in the server's order.
const CHANGE_SUBJECT_WORDS: Record<string, string> = {
  owner: 'the owner', persona: 'the persona', outbound: 'outbound sending',
}

function listWords(words: string[]): string {
  return words.length <= 1 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`
}

/** "Last changed by <name>, <when>": the name only when the server resolved
 *  one. When the change left the binding alone, the line names what it moved
 *  ("…: the owner and outbound sending"), so it never reads as a policy edit. */
export function lastChangeLine(change: LastChange): string {
  const at = new Date(change.changed_at)
  const when = Number.isNaN(at.getTime()) ? change.changed_at : at.toLocaleString()
  const line = change.changed_by_name ? `Last changed by ${change.changed_by_name}, ${when}` : `Last changed ${when}`
  const fields = change.fields
  if (!fields || fields.includes('binding')) return line
  const words = fields.map((field) => CHANGE_SUBJECT_WORDS[field]).filter((word): word is string => Boolean(word))
  return words.length > 0 ? `${line}: ${listWords(words)}` : line
}
